import type { ReactNode } from 'react';

import type { IconComponent } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Skeleton, SkeletonText } from '@infinex/ui/components';

type WalletLabelPillProps = {
  /**
   * The label text/element to display
   */
  label: ReactNode;

  /**
   * Static icon component to display on the left side.
   * Use this for simple, non-customizable icons where WalletLabelPill should
   * apply the standard fill and size.
   */
  icon?: IconComponent;

  /**
   * Fully rendered icon node to display on the left side.
   * Use this when the caller needs custom rendering, such as
   * CustodyEmblemFromWalletId. When provided, this takes precedence over icon.
   */
  iconNode?: ReactNode;

  /**
   * Visual prominence level
   * - `low`: No border
   * - `medium`: With border (default)
   */
  prominence?: 'low' | 'medium';

  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean;

  /**
   * Additional class names for the container
   */
  className?: string;
};

/**
 * A pill-shaped label component for displaying wallet/portfolio information.
 *
 * @example
 * ```tsx
 * <WalletLabelPill
 *   label="Total portfolio"
 *   icon={PortfolioFillIcon}
 * />
 * ```
 */
export const WalletLabelPill = ({
  label,
  icon: Icon,
  iconNode,
  prominence = 'medium',
  isLoading = false,
  className,
}: WalletLabelPillProps) => {
  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          'inline-flex h-8 w-32 items-center gap-x-1.5 rounded-full px-2.5',
          className
        )}
      >
        <SkeletonText size="sm" />
      </Skeleton>
    );
  }

  return (
    <div
      className={cn(
        'box-border inline-flex h-8 max-w-64 items-center gap-x-1.5 rounded-full px-2.5',
        prominence === 'medium' && 'border-secondary border-2',
        className
      )}
    >
      {iconNode ?? (Icon ? <Icon fill="brand" size="2xsmall" /> : null)}

      <span className="text-default truncate text-sm font-semibold">
        {label}
      </span>
    </div>
  );
};
